import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ClipboardList, FileText, AlertTriangle, CheckCircle, Clock, ArrowUpRight } from 'lucide-react';
import { api } from '../utils/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import Card from '../components/ui/Card.jsx';
import StatusBadge from '../components/ui/Badge.jsx';
import { formatIST, formatBytes } from '../utils/formatters.js';
import { isSuperAdmin, isAdmin } from '../utils/permissions.js';
import { GP_TYPE_LABELS } from '../utils/constants.js';

function StatCard({ label, value, icon: Icon, color, to }) {
  const content = (
    <div className={`bg-white rounded-xl border-l-4 ${color} shadow-sm p-5 flex items-center justify-between`}>
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value ?? '—'}</p>
      </div>
      <div className="opacity-20">
        <Icon className="w-10 h-10" />
      </div>
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function StorageWidget({ storage }) {
  if (!storage) return null;
  const pct = Math.min((storage.used / storage.quota) * 100, 100);
  const color = pct > 85 ? 'bg-red-500' : pct > 65 ? 'bg-orange-400' : 'bg-teal-500';
  return (
    <Card title="R2 Storage">
      <div className="p-4 space-y-2">
        <div className="flex justify-between text-sm font-medium">
          <span>{formatBytes(storage.used)}</span>
          <span className="text-gray-400">{formatBytes(storage.quota)}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div className={`${color} h-3 rounded-full transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-gray-400">{pct.toFixed(1)}% used · {storage.fileCount} files</p>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();

  const { data: dash } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/dashboard').then(r => r.data.data),
  });

  const { data: storage } = useQuery({
    queryKey: ['storage'],
    queryFn: () => api.get('/files/storage').then(r => r.data.data),
    enabled: isSuperAdmin(user) || isAdmin(user),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">Welcome back, {user?.name}</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Open Gate Passes" value={dash?.openGPs} icon={ClipboardList} color="border-teal-500" to="/gate-passes?status=APPROVED" />
        <StatCard label="Pending Approval" value={dash?.pendingApproval} icon={Clock} color="border-yellow-400" to="/gate-passes?status=SUBMITTED" />
        <StatCard label="Active Deliveries" value={dash?.activeDeliveries} icon={FileText} color="border-blue-500" to="/delivery-notes" />
        <StatCard label="Overdue" value={dash?.overdue} icon={AlertTriangle} color="border-red-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Gate Passes */}
        <div className="lg:col-span-2">
          <Card title="Recent Gate Passes" action={<Link to="/gate-passes" className="text-xs text-teal-600 hover:underline flex items-center gap-1">View all <ArrowUpRight className="w-3 h-3" /></Link>}>
            {dash?.recentGPs?.length ? (
              <div className="divide-y">
                {dash.recentGPs.map(gp => (
                  <Link key={gp.id} to={`/gate-passes/${gp.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                    <div>
                      <p className="font-mono text-sm font-semibold text-teal-700">{gp.number}</p>
                      <p className="text-xs text-gray-500">{GP_TYPE_LABELS[gp.type]} · {gp.sourceLocation?.code}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={gp.status} />
                      <span className="text-xs text-gray-400">{formatIST(gp.createdAt, 'dd MMM')}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">No recent gate passes</p>
            )}
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Overdue deliveries */}
          <Card title="Overdue Deliveries">
            {dash?.overdueDeliveries?.length ? (
              <div className="divide-y">
                {dash.overdueDeliveries.slice(0, 4).map(dn => (
                  <Link key={dn.id} to={`/delivery-notes/${dn.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <div>
                      <p className="font-mono text-xs font-semibold text-orange-600">{dn.number}</p>
                      <p className="text-xs text-gray-500 truncate max-w-40">{dn.contentDescription}</p>
                    </div>
                    <span className="badge badge-overdue">Overdue</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="px-4 py-6 text-center text-gray-400 text-sm">No overdue deliveries</p>
            )}
          </Card>

          {/* Pending returnables */}
          {dash?.upcomingReturnables?.length > 0 && (
            <Card title="Upcoming Returns">
              <div className="divide-y">
                {dash.upcomingReturnables.slice(0, 4).map(gp => (
                  <Link key={gp.id} to={`/gate-passes/${gp.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <div>
                      <p className="font-mono text-xs font-semibold text-blue-700">{gp.number}</p>
                      <p className="text-xs text-gray-500">{gp.expectedReturnDate?.slice(0, 10)}</p>
                    </div>
                    <span className="badge badge-pending">Due soon</span>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {/* Storage widget — Admin+ only */}
          {(isSuperAdmin(user) || isAdmin(user)) && <StorageWidget storage={storage} />}
        </div>
      </div>
    </div>
  );
}
